import { useEffect, useRef } from "react";

import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";
import { getRedirectDomain } from "../useSubmitPayment/useSubmitPayment";

const HOST: string = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
const END_POINT: string = process.env.API_PAYMENT ?? "";

const url: string = `${HOST}${END_POINT}`;

interface ApplePayPaymentData {
	paymentData: {
		version: string;
		data: string;
		signature: string;
		header: {
			ephemeralPublicKey: string;
			publicKeyHash: string;
			transactionId: string;
		};
	};
	paymentMethod: {
		displayName: string;
		network: string;
		type: string;
	};
	transactionIdentifier: string;
}

interface AdyenApplePayPaymentRequest {
	paymentMethod: {
		type: "applepay";
		applePayToken: string;
	};
	amount: {
		currency: string;
		value: number;
	};
	countryCode: string;
	merchantAccount: string;
	returnUrl: string;
	hasAction: boolean;
	reference: string;
}

interface AdyenPaymentResponse {
	resultCode: string;
	action?: any;
	order?: any;
	donationToken?: string;
	pspReference?: string;
	merchantReference?: string;
	refusalReason?: string;
	refusalReasonCode?: string;
}

const usePostAdyenApplePayment = () => {
	const { state: paymentReference } = usePaymentContext((state) => state.paymentReference);
	const { state: paymentData } = usePaymentContext((state) => state.paymentData);
	const {
		state: { user, config },
	} = useConfigContext((state) => state);
	const payload = {
		url,
		axiosConfig: addJWTInConfig(undefined, user?.session_id?.toString() ?? ""),
		triggerOnMount: false,
		swrConfig: {
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			revalidateIfStale: false,
			shouldRetryOnError: false,
			errorRetryInterval: 0,
			errorRetryCount: 0,
		},
	};

	const actionRef = useRef<any>(null);
	const { data: response, postData } = useApiRequestPost<
		AdyenPaymentResponse,
		AdyenApplePayPaymentRequest
	>(payload);

	const submitApplePayment = (applePayData: any, actions: any) => {
		// Extract the complete Apple Pay payment data
		const applePayPaymentData = applePayData.authorizedEvent.payment.token.paymentData;

		// Convert to base64 encoded string as required by Adyen
		const applePayTokenString = btoa(JSON.stringify(applePayPaymentData));

		const requestPayload: any = {
			paymentMethod: {
				type: "applepay",
				applePayToken: applePayTokenString,
			},
			amount: {
				currency: "SGD",
				value: paymentData?.orders_figures?.net_value ?? 0,
			},
			merchantAccount: config.adyen_merchant_name?.name,
			returnUrl: `${globalThis.location.origin}${PATH.ORDER_CONFIRMATION}`,
			redirectDomain: getRedirectDomain(),
			reference: paymentReference ?? "",
			paymentReference,
		};

		postData(requestPayload);
		actionRef.current = actions;
	};

	useEffect(() => {
		if (!response) return;

		const { resultCode } = response;

		if (actionRef.current) {
			// Handle different result codes
			switch (resultCode) {
				case "Authorised":
					actionRef.current.resolve(response);
					break;

				case "Refused":
				case "Error":
					actionRef.current.reject({
						resultCode,
						refusalReasonCode: response.refusalReasonCode,
					});
					break;

				case "RedirectShopper":
				case "IdentifyShopper":
				case "ChallengeShopper":
					actionRef.current.resolve({
						response,
					});
					break;

				default:
					actionRef.current.resolve(response);
					break;
			}
		}
	}, [response, actionRef]);

	return {
		submitApplePayment,
		isLoading: !!response,
		response,
	};
};

export default usePostAdyenApplePayment;
